import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Header from "../../component/users/Header";
import { Box, Container } from "@mui/material";
import Options from "../../component/users/Home/Options";
import Crousel from "../../component/users/Home/Crousel";
import GoalVideos from "../../component/users/Home/GoalVideos";
import HomeSkeleton from "../../component/users/Home/Skeleton";
import HomeBannerChooser from "../../component/users/Home/HomeBannerChooser";
import GuestUserModal from "../../component/Modals/user/guestUserModal/Modal";
import { ToastContainer } from "react-toastify";
import {
  getHomeFlow,
  setFootballFlow,
  HOME_FLOW_RESET_EVENT,
} from "../../utils/homeFlow";

const Home = () => {
  const user = useSelector((state) => state.user.loading);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const chooser = useSelector((state) => state.homeBannerChooser);
  const location = useLocation();

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showExistingFlow, setShowExistingFlow] = useState(
    () => getHomeFlow() === "existing"
  );

  // Keep chooser in sync when "Football / Pageant" clears flow
  // (especially when already on "/" so the page does not remount).
  useEffect(() => {
    const syncFlow = () => {
      setShowExistingFlow(getHomeFlow() === "existing");
    };
    syncFlow();
    window.addEventListener(HOME_FLOW_RESET_EVENT, syncFlow);
    return () => window.removeEventListener(HOME_FLOW_RESET_EVENT, syncFlow);
  }, [location.key, location.state]);

  // Guest trial modal: only when the *current JWT* is a guest (has device_id).
  // Stale mynetgoals.com-userData must not block registered users.
  useEffect(() => {
    const token = localStorage.getItem("mynetgoals.com-token");
    if (!token) {
      setShowGuestModal(false);
      return;
    }

    let session;
    try {
      session = jwtDecode(token);
    } catch (e) {
      setShowGuestModal(false);
      return;
    }

    if (!session?.device_id) {
      localStorage.removeItem("mynetgoals.com-userData");
      setShowGuestModal(false);
      return;
    }

    try {
      const guestRaw = localStorage.getItem("mynetgoals.com-userData");
      const parsed = guestRaw ? JSON.parse(guestRaw) : null;
      const trialSource = parsed?.trial_end_date || session?.trial_end_date;
      const trialEnd = trialSource ? new Date(trialSource) : null;
      if (
        trialEnd &&
        !Number.isNaN(trialEnd.getTime()) &&
        trialEnd.getTime() < Date.now()
      ) {
        setShowGuestModal(true);
      } else {
        setShowGuestModal(false);
      }
    } catch (e) {
      localStorage.removeItem("mynetgoals.com-userData");
      setShowGuestModal(false);
    }
  }, [isAuthenticated, location.key]);

  const homeBanners = Array.isArray(chooser?.data?.data)
    ? chooser.data.data
    : [];
  const chooserFailed = Boolean(chooser?.error);
  // Skip chooser when none configured or load failed — keep existing Home working
  const shouldShowChooser =
    !showExistingFlow &&
    !chooserFailed &&
    (chooser.loading || homeBanners.length > 0);

  return (
    <>
      <ToastContainer />
      <Header hideMainNav={shouldShowChooser} showChangeSection={showExistingFlow} />
      {user ? (
        <HomeSkeleton />
      ) : (
        <Box className="userLayout">
          <Container
            disableGutters={true}
            maxWidth="lg"
            sx={{
              background: shouldShowChooser ? "#0b0d16" : "#242743",
              paddingBottom: shouldShowChooser ? "40px" : "116px",
              minHeight: shouldShowChooser ? "100vh" : undefined,
            }}
          >
            {shouldShowChooser ? (
              <HomeBannerChooser
                onSelectExisting={() => {
                  setFootballFlow();
                  setShowExistingFlow(true);
                }}
              />
            ) : (
              <>
                <Options />
                <Box className="homePage-content">
                  <Crousel />
                  <GoalVideos />
                </Box>
              </>
            )}
          </Container>
        </Box>
      )}

      <GuestUserModal open={showGuestModal} setOpen={setShowGuestModal} />
    </>
  );
};

export default Home;
